import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { Button } from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CloseIcon from "@mui/icons-material/Close";
import { FaTimes, FaFile, FaTag, FaCheck } from "react-icons/fa";
import { HiHome } from "react-icons/hi2";
import { RxCross2 } from "react-icons/rx";
import { toast } from "react-toastify";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

export const tagsToMetaData = (tags) => {
  if (!tags?.length) return { room: null, milestones: [], tags: [] };
  const nameTag = tags.find((t) => t.type === "name");
  const milestoneTags = tags.filter((t) => t.type === "milestone");
  const tagsWithPos = tags.map((t) => ({
    type: t.type,
    label: t.label,
    refId: t.refId ?? null,
    x: t.x,
    y: t.y,
  }));
  return {
    room: nameTag?.label ?? null,
    milestones: milestoneTags.map((t) => ({
      id: t.refId,
      milestone_name: t.label,
    })),
    tags: tagsWithPos,
  };
};

const metaDataToTags = (metaData) => {
  if (!metaData) return [];
  if (
    metaData.tags &&
    Array.isArray(metaData.tags) &&
    metaData.tags.length > 0
  ) {
    return metaData.tags.map((t, i) => ({
      id: t.id || `tag_${i}_${Date.now()}`,
      type: t.type,
      label: t.label,
      refId: t.refId ?? null,
      x: t.x ?? 10,
      y: t.y ?? 10 + i * 10,
    }));
  }
  const tags = [];
  let y = 10;
  if (metaData.room) {
    tags.push({
      id: `tag_room_${Date.now()}`,
      type: "name",
      label: metaData.room,
      x: 10,
      y,
      refId: null,
    });
    y += 15;
  }
  (metaData.milestones || []).forEach((m, i) => {
    tags.push({
      id: `tag_m_${m.id}_${i}`,
      type: "milestone",
      label: m.milestone_name || m.name,
      refId: m.id,
      x: 10,
      y: y + i * 10,
    });
  });
  return tags;
};

const getRoomName = (meta) => meta?.room_name ?? meta?.room ?? null;

export default function UploadSection({
  uploadedFiles,
  uploading,
  handleFileChange,
  handleDelete,
  isEdit = false,
  disabled = false,
  onFileMetaDataChange,
  updateId,
  effectiveUpdateId,
  onSaveAnnotatedImage,
  isViewMode = false,
  activitySubTab: activitySubTabProp,
  setActivitySubTab: setActivitySubTabProp,
  otherImages = [],
  otherPdfFiles = [],
  projectDetails,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [activitySubTabLocal, setActivitySubTabLocal] = useState("annotate");
  const activitySubTab = activitySubTabProp ?? activitySubTabLocal;
  const setActivitySubTab = setActivitySubTabProp ?? setActivitySubTabLocal;

  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [savingImage, setSavingImage] = useState(false);
  const [annotatingIndex, setAnnotatingIndex] = useState(null);
  const [annotations, setAnnotations] = useState({});
  const [pendingPin, setPendingPin] = useState(null);
  const [pinStep, setPinStep] = useState(null);
  const [nameInput, setNameInput] = useState("");
  const [selectedGalleryRoom, setSelectedGalleryRoom] = useState(null);
  const [viewingImage, setViewingImage] = useState(null);
  const nameInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const prevMetaJsonRef = useRef("");
  const prevSyncedIdRef = useRef(null);
  const selectNextAfterSaveRef = useRef(null);

  const data = projectDetails || {};
  const effectiveId = updateId || effectiveUpdateId;

  const imageFiles = useMemo(
    () =>
      uploadedFiles?.filter(
        (f) =>
          f.file_extention?.startsWith("image/") ||
          f.type?.startsWith("image/"),
      ) || [],
    [uploadedFiles],
  );

  const annotatingFile =
    annotatingIndex !== null ? uploadedFiles[annotatingIndex] : null;
  const annotatingId = annotatingFile?.uniqueId || `idx_${annotatingIndex}`;
  const currentTags = annotations[annotatingId] || [];
  const hasName = currentTags.some((t) => t.type === "name");
  const isAnnotatingPdf =
    annotatingFile &&
    (annotatingFile.file_extention === "application/pdf" ||
      annotatingFile.type === "application/pdf");
  const canSaveAnnotated =
    annotatingFile &&
    !annotatingFile.id &&
    annotatingFile.file &&
    onSaveAnnotatedImage &&
    effectiveId &&
    (hasName || isAnnotatingPdf);

  const pendingFiles = useMemo(
    () => (uploadedFiles || []).filter((f) => !f.id),
    [uploadedFiles],
  );

  const savedImagesWithRoom = useMemo(
    () =>
      (uploadedFiles || []).filter(
        (f) =>
          f.id &&
          getRoomName(f.meta_data) &&
          (f.file_extention?.startsWith("image/") ||
            f.type?.startsWith("image/")),
      ),
    [uploadedFiles],
  );

  const roomGroups = useMemo(() => {
    const groups = {};
    savedImagesWithRoom.forEach((img) => {
      const room = getRoomName(img.meta_data) || "Unnamed room";
      if (!groups[room]) groups[room] = [];
      groups[room].push(img);
    });
    return groups;
  }, [savedImagesWithRoom]);

  const GALLERY_TAB_PDF = "__pdf__";
  const GALLERY_TAB_OTHER = "__other__";

  const normalizedOtherImages = useMemo(
    () =>
      (otherImages || []).map((img) => ({
        ...img,
        id: img.id,
        url: img.path || img.url,
        path: img.path || img.url,
        uniqueId: `other_${img.id}`,
        meta_data: {},
        created_at: img.created_at,
        file_extention: img.file_extention,
      })),
    [otherImages],
  );
  const normalizedOtherPdfs = useMemo(
    () =>
      (otherPdfFiles || []).map((pdf) => ({
        ...pdf,
        id: pdf.id,
        url: pdf.path || pdf.url,
        path: pdf.path || pdf.url,
        uniqueId: `other_pdf_${pdf.id}`,
        meta_data: {},
        created_at: pdf.created_at,
        file_extention: pdf.file_extention || "application/pdf",
      })),
    [otherPdfFiles],
  );
  const hasOtherMedia =
    normalizedOtherImages.length > 0 || normalizedOtherPdfs.length > 0;

  const savedPdfs = useMemo(
    () =>
      (uploadedFiles || []).filter(
        (f) =>
          f.id &&
          (f.file_extention === "application/pdf" ||
            f.type === "application/pdf"),
      ),
    [uploadedFiles],
  );

  const existingRoomNames = useMemo(() => {
    const fromFiles = (uploadedFiles || [])
      .map((f) => getRoomName(f.meta_data))
      .filter(Boolean);
    const fromAnnotations = Object.values(annotations)
      .flat()
      .filter((t) => t.type === "name")
      .map((t) => t.label);
    return [...new Set([...fromFiles, ...fromAnnotations])].sort((a, b) =>
      a.localeCompare(b),
    );
  }, [uploadedFiles, annotations]);

  const goNext = () =>
    setActiveIndex((i) => (i + 1) % (imageFiles.length || 1));
  const goPrev = () =>
    setActiveIndex((i) =>
      (i - 1 + (imageFiles.length || 1)) % (imageFiles.length || 1),
    );

  useEffect(() => {
    if (!isViewerOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setIsViewerOpen(false);
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isViewerOpen, activeIndex]);

  useEffect(() => {
    if (pinStep === "name" && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [pinStep]);

  useEffect(() => {
    if (annotatingIndex === null || !annotatingFile) return;
    const id = annotatingFile.uniqueId || `idx_${annotatingIndex}`;
    const meta = annotatingFile.meta_data;
    const hasMeta =
      meta && (meta.room || (meta.milestones && meta.milestones.length));
    setAnnotations((prev) => {
      if (!hasMeta || (prev[id] && prev[id].length > 0)) return prev;
      return { ...prev, [id]: metaDataToTags(meta) };
    });
  }, [annotatingIndex]);

  useEffect(() => {
    if (annotatingIndex === null || !onFileMetaDataChange) return;
    const file = uploadedFiles?.[annotatingIndex];
    if (!file) return;
    const id = file.uniqueId || `idx_${annotatingIndex}`;
    if (prevSyncedIdRef.current !== id) {
      prevSyncedIdRef.current = id;
      prevMetaJsonRef.current = "";
    }
    const tags = annotations[id] || [];
    const meta = tagsToMetaData(tags);
    const metaJson = JSON.stringify(meta);
    if (metaJson === prevMetaJsonRef.current) return;
    prevMetaJsonRef.current = metaJson;
    onFileMetaDataChange(id, meta);
  }, [annotations, annotatingIndex, uploadedFiles, onFileMetaDataChange]);

  useEffect(() => {
    const uidToSelect = selectNextAfterSaveRef.current;
    if (uidToSelect != null) {
      const idx = (uploadedFiles || []).findIndex(
        (f) => f.uniqueId === uidToSelect,
      );
      if (idx >= 0) setAnnotatingIndex(idx);
      return;
    }
    if (annotatingIndex !== null && !uploadedFiles?.[annotatingIndex]) {
      const next = (uploadedFiles || []).findIndex((f) => !f.id);
      setAnnotatingIndex(next >= 0 ? next : null);
    } else if (annotatingIndex === null && pendingFiles.length > 0) {
      const firstPendingIdx = (uploadedFiles || []).findIndex((f) => !f.id);
      if (firstPendingIdx >= 0) setAnnotatingIndex(firstPendingIdx);
    }
  }, [uploadedFiles, pendingFiles.length, annotatingIndex]);

  const handleInputChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (files.length > 25) {
      toast.error("Maximum 25 files allowed at a time.");
      e.target.value = "";
      return;
    }
    handleFileChange({ target: { files } });
    e.target.value = "";
  };

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      const files = Array.from(e.dataTransfer?.files || []);
      if (files.length > 0) handleFileChange({ target: { files } });
    },
    [handleFileChange],
  );
  const onDragOver = (e) => e.preventDefault();

  const handleImageClick = (e) => {
    if (disabled) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setPendingPin({ x, y });
    setPinStep(hasName ? "milestone" : "name");
    setNameInput("");
  };

  const addNameTag = (label) => {
    const roomLabel =
      typeof label === "string" ? label.trim() : nameInput.trim();
    if (!pendingPin || !roomLabel) return;
    const tag = {
      id: `tag_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
      type: "name",
      label: roomLabel,
      x: pendingPin.x,
      y: pendingPin.y,
    };
    setAnnotations((prev) => ({
      ...prev,
      [annotatingId]: [...(prev[annotatingId] || []), tag],
    }));
    setPendingPin(null);
    setPinStep(null);
    setNameInput("");
  };

  const matchingRoomNames = useMemo(() => {
    const q = (nameInput || "").trim().toLowerCase();
    if (!q) return existingRoomNames.slice(0, 10);
    return existingRoomNames
      .filter((r) => r.toLowerCase().includes(q))
      .slice(0, 10);
  }, [existingRoomNames, nameInput]);
  const canCreateRoom = (nameInput || "").trim().length > 0;

  const addMilestoneTag = (milestone) => {
    if (!pendingPin) return;
    const tag = {
      id: `tag_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
      type: "milestone",
      label: milestone.milestone_name || milestone.name,
      refId: milestone.id,
      x: pendingPin.x,
      y: pendingPin.y,
    };
    setAnnotations((prev) => ({
      ...prev,
      [annotatingId]: [...(prev[annotatingId] || []), tag],
    }));
    setPendingPin(null);
    setPinStep(null);
  };

  const removeTag = (tagId) => {
    setAnnotations((prev) => ({
      ...prev,
      [annotatingId]: (prev[annotatingId] || []).filter((t) => t.id !== tagId),
    }));
  };

  const getPopoverStyle = (pin) => {
    const style = { position: "absolute", zIndex: 50 };
    if (pin.x > 65) style.right = `${100 - pin.x}%`;
    else style.left = `${pin.x}%`;
    if (pin.y > 60) style.bottom = `${100 - pin.y}%`;
    else style.top = `${pin.y}%`;
    return style;
  };

  const handleSaveAnnotated = async () => {
    if (!canSaveAnnotated) return;
    const currentIdxInPending = pendingFiles.findIndex(
      (f) => f.uniqueId === annotatingFile.uniqueId,
    );
    const nextFile = pendingFiles[currentIdxInPending + 1];
    setSavingImage(true);
    const meta = isAnnotatingPdf
      ? { room: null, milestones: [], tags: [] }
      : tagsToMetaData(currentTags);
    try {
      await onSaveAnnotatedImage(annotatingFile, meta);
      selectNextAfterSaveRef.current = nextFile?.uniqueId ?? null;
      setAnnotatingIndex(null);
      setPendingPin(null);
      setPinStep(null);
    } catch (err) {
      toast.error(err?.message || "Failed to save");
    } finally {
      setSavingImage(false);
    }
  };

  const formatImageDate = (item) => {
    if (item.created_at) return new Date(item.created_at);
    return new Date();
  };

  const imageStyle = {
    width: "100%",
    height: 140,
    objectFit: "cover",
    borderRadius: 8,
    backgroundColor: "#f4f4f4",
  };

  const milestoneList = data?.milestone || [];

  if (activitySubTab === "annotate") {
    return (
      <div style={{ width: "100%", paddingBottom: 8 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 3fr",
            gap: 24,
          }}
        >
          <div style={{ marginTop: 8 }}>
            <p
              style={{
                fontSize: 14,
                fontWeight: 500,
                color: "#0d0d0e",
                marginBottom: 8,
              }}
            >
              {pendingFiles.length} image{pendingFiles.length !== 1 ? "s" : ""}
            </p>
            {isMobile ? (
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  overflowX: "auto",
                  paddingBottom: 8,
                }}
              >
                {pendingFiles.length === 0 && (
                  <div
                    onDragOver={onDragOver}
                    onDrop={onDrop}
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      flexShrink: 0,
                      minWidth: 200,
                      border: "2px dashed #d1d5db",
                      borderRadius: 12,
                      padding: 24,
                      textAlign: "center",
                      fontSize: 14,
                      color: "#6b7280",
                      cursor: disabled ? "default" : "pointer",
                    }}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleInputChange}
                      style={{ display: "none" }}
                    />
                    Click or drag to upload
                  </div>
                )}
                {pendingFiles.map((item, idx) => {
                  const idxInFull = uploadedFiles.findIndex(
                    (f) => f.uniqueId === item.uniqueId,
                  );
                  const isPdf =
                    item.file_extention === "application/pdf" ||
                    item.type === "application/pdf";
                  const isImage =
                    item.file_extention?.startsWith("image/") ||
                    item.type?.startsWith("image/");
                  const isSelected = annotatingIndex === idxInFull;
                  return (
                    <div
                      key={item.uniqueId || idx}
                      onClick={() => {
                        if (isImage || isPdf) {
                          selectNextAfterSaveRef.current = null;
                          setAnnotatingIndex(idxInFull);
                          setPendingPin(null);
                          setPinStep(null);
                        }
                      }}
                      style={{
                        position: "relative",
                        flexShrink: 0,
                        width: 96,
                        borderRadius: 12,
                        border: `2px solid ${isSelected ? "#2563eb" : "#e5e7eb"}`,
                        overflow: "hidden",
                        cursor: "pointer",
                        background: isSelected ? "rgba(37,99,235,0.1)" : "#fff",
                      }}
                    >
                      <div
                        style={{
                          aspectRatio: "1",
                          width: 96,
                          background: "#f3f4f6",
                        }}
                      >
                        {isPdf ? (
                          <div
                            style={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#d32f2f",
                            }}
                          >
                            <PictureAsPdfIcon style={{ fontSize: 32 }} />
                          </div>
                        ) : isImage ? (
                          <img
                            src={item.url || item.path}
                            alt=""
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#9ca3af",
                            }}
                          >
                            <FaFile size={20} />
                          </div>
                        )}
                      </div>
                      {!disabled && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(item);
                          }}
                          style={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            padding: 6,
                            borderRadius: "50%",
                            background: "#ef4444",
                            color: "#fff",
                            border: "none",
                            cursor: "pointer",
                            display: "flex",
                          }}
                        >
                          <FaTimes size={10} />
                        </button>
                      )}
                    </div>
                  );
                })}
                {pendingFiles.length > 0 && !disabled && (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      flexShrink: 0,
                      width: 96,
                      height: 96,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "2px dashed #d1d5db",
                      borderRadius: 12,
                      cursor: "pointer",
                    }}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleInputChange}
                      style={{ display: "none" }}
                    />
                    <span style={{ fontSize: 24, color: "#6b7280" }}>+</span>
                  </div>
                )}
              </div>
            ) : (
              <div
                style={{
                  maxHeight: "70vh",
                  overflowY: "auto",
                  paddingTop: 16,
                }}
              >
                {pendingFiles.length === 0 && (
                  <div
                    onDragOver={onDragOver}
                    onDrop={onDrop}
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      border: "2px dashed #d1d5db",
                      borderRadius: 12,
                      padding: 24,
                      textAlign: "center",
                      fontSize: 14,
                      color: "#6b7280",
                      cursor: disabled ? "default" : "pointer",
                    }}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleInputChange}
                      style={{ display: "none" }}
                    />
                    Click or drag to upload images
                  </div>
                )}
                {pendingFiles.map((item, idx) => {
                  const idxInFull = uploadedFiles.findIndex(
                    (f) => f.uniqueId === item.uniqueId,
                  );
                  const isPdf =
                    item.file_extention === "application/pdf" ||
                    item.type === "application/pdf";
                  const isImage =
                    item.file_extention?.startsWith("image/") ||
                    item.type?.startsWith("image/");
                  const fileTags = annotations[item.uniqueId] || [];
                  const hasRoom = fileTags.some((t) => t.type === "name");
                  const isSelected = annotatingIndex === idxInFull;
                  return (
                    <div
                      key={item.uniqueId || idx}
                      onClick={() => {
                        if (isImage || isPdf) {
                          selectNextAfterSaveRef.current = null;
                          setAnnotatingIndex(idxInFull);
                          setPendingPin(null);
                          setPinStep(null);
                        }
                      }}
                      style={{
                        position: "relative",
                        display: "flex",
                        gap: 8,
                        padding: 8,
                        borderRadius: 8,
                        border: `1px solid ${isSelected ? "#2563eb" : "#e5e7eb"}`,
                        cursor: "pointer",
                        background: isSelected ? "#eff6ff" : "#fff",
                        marginBottom: 8,
                      }}
                    >
                      <div
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: 8,
                          overflow: "hidden",
                          flexShrink: 0,
                          background: "#f3f4f6",
                        }}
                      >
                        {isPdf ? (
                          <div
                            style={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#d32f2f",
                            }}
                          >
                            <PictureAsPdfIcon style={{ fontSize: 20 }} />
                          </div>
                        ) : isImage ? (
                          <img
                            src={item.url || item.path}
                            alt=""
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#9ca3af",
                            }}
                          >
                            <FaFile size={18} />
                          </div>
                        )}
                      </div>
                      <div
                        style={{
                          flex: 1,
                          minWidth: 0,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                        }}
                      >
                        <p
                          style={{
                            fontSize: 12,
                            color: "#6b7280",
                            margin: 0,
                          }}
                        >
                          {formatImageDate(item).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </p>
                        {!hasRoom && isImage ? (
                          <span
                            style={{
                              fontSize: 10,
                              color: "#d97706",
                              fontWeight: 500,
                              marginTop: 2,
                            }}
                          >
                            Needs room tag
                          </span>
                        ) : (
                          <span
                            style={{
                              fontSize: 10,
                              color: "#6b7280",
                              marginTop: 2,
                            }}
                          >
                            {fileTags.length} tag
                            {fileTags.length !== 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                      {!disabled && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(item);
                          }}
                          style={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            padding: 4,
                            borderRadius: "50%",
                            background: "#ef4444",
                            color: "#fff",
                            border: "none",
                            cursor: "pointer",
                            display: "flex",
                          }}
                        >
                          <FaTimes size={10} />
                        </button>
                      )}
                    </div>
                  );
                })}
                {pendingFiles.length > 0 && !disabled && (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      width: 48,
                      height: 48,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "2px dashed #d1d5db",
                      borderRadius: 8,
                      cursor: "pointer",
                    }}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleInputChange}
                      style={{ display: "none" }}
                    />
                    <span style={{ fontSize: 24, color: "#6b7280" }}>+</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              minHeight: "60vh",
              marginTop: isMobile ? 20 : 0,
            }}
          >
            <p
              style={{
                fontSize: 14,
                color: "#6b7280",
                marginBottom: 8,
              }}
            >
              {annotatingFile
                ? isAnnotatingPdf
                  ? "PDF file – save to upload"
                  : hasName
                    ? "Tag a milestone"
                    : "Tag the room"
                : ""}
            </p>
            {annotatingFile ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  overflow: "hidden",
                  background: "#fff",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px 16px",
                    borderBottom: "1px solid #e5e7eb",
                    background: "#f9fafb",
                  }}
                >
                  {!isAnnotatingPdf && (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "4px 10px",
                        borderRadius: 9999,
                        fontSize: 12,
                        fontWeight: 500,
                        background: hasName ? "#dcfce7" : "#2563eb",
                        color: hasName ? "#166534" : "#fff",
                      }}
                    >
                      <HiHome size={10} />
                      {hasName
                        ? "Step 2: Tag a milestone"
                        : "Step 1: Tag a Room"}
                    </span>
                  )}
                </div>
                {isAnnotatingPdf ? (
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 32,
                    }}
                  >
                    <a
                      href={annotatingFile.url || annotatingFile.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 12,
                        padding: 24,
                        border: "2px dashed #e5e7eb",
                        borderRadius: 12,
                        textDecoration: "none",
                        color: "inherit",
                      }}
                    >
                      <PictureAsPdfIcon
                        style={{ fontSize: 48, color: "#d32f2f" }}
                      />
                      <span
                        style={{
                          fontSize: 14,
                          fontWeight: 500,
                          color: "#374151",
                        }}
                      >
                        PDF – click to open
                      </span>
                    </a>
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                        flex: 1,
                        overflow: "auto",
                        padding: 16,
                      }}
                    >
                      <div
                        role="button"
                        tabIndex={0}
                        onKeyDown={() => { }}
                        style={{
                          position: "relative",
                          display: "inline-block",
                          width: "100%",
                          cursor: "crosshair",
                        }}
                        onClick={handleImageClick}
                      >
                        <img
                          src={annotatingFile.url || annotatingFile.path}
                          alt=""
                          style={{
                            width: "100%",
                            maxHeight: "55vh",
                            borderRadius: 8,
                            objectFit: "contain",
                            background: "#f9fafb",
                          }}
                          draggable={false}
                        />
                        <div
                          style={{
                            position: "absolute",
                            top: 12,
                            right: 12,
                            background: "rgba(0,0,0,0.7)",
                            color: "#fff",
                            padding: "4px 10px",
                            borderRadius: 6,
                            fontSize: 12,
                            fontWeight: 500,
                          }}
                        >
                          {formatImageDate(annotatingFile).toLocaleDateString(
                            "en-GB",
                            {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            },
                          )}
                        </div>
                        <div
                          style={{
                            position: "absolute",
                            bottom: 12,
                            left: 12,
                            background: "rgba(0,0,0,0.6)",
                            color: "#fff",
                            padding: "6px 10px",
                            borderRadius: 6,
                            fontSize: 12,
                          }}
                        >
                          {hasName
                            ? "Click to tag a milestone"
                            : "Click to tag the room"}
                        </div>
                        {currentTags.map((tag) => (
                          <div
                            key={tag.id}
                            style={{
                              position: "absolute",
                              left: `${tag.x}%`,
                              top: `${tag.y}%`,
                              transform: "translate(-50%, -100%)",
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                                padding: "4px 8px",
                                borderRadius: 9999,
                                color: "#fff",
                                fontSize: 11,
                                fontWeight: 500,
                                background:
                                  tag.type === "name" ? "#2563eb" : "#16a34a",
                              }}
                            >
                              {tag.type === "name" ? (
                                <HiHome size={10} />
                              ) : (
                                <FaTag size={10} />
                              )}
                              {tag.label}
                              {!disabled && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeTag(tag.id);
                                  }}
                                  style={{
                                    marginLeft: 2,
                                    background: "none",
                                    border: "none",
                                    borderRadius: "50%",
                                    padding: 2,
                                    cursor: "pointer",
                                    display: "flex",
                                    color: "#fff",
                                  }}
                                >
                                  <FaTimes size={8} />
                                </button>
                              )}
                            </div>
                            <div
                              style={{
                                width: 2,
                                height: 12,
                                margin: "0 auto",
                                background:
                                  tag.type === "name" ? "#2563eb" : "#16a34a",
                              }}
                            />
                            <div
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                margin: "-2px auto 0",
                                background:
                                  tag.type === "name" ? "#2563eb" : "#16a34a",
                              }}
                            />
                          </div>
                        ))}
                        {pendingPin && (
                          <>
                            <div
                              style={{
                                position: "absolute",
                                left: `${pendingPin.x}%`,
                                top: `${pendingPin.y}%`,
                                transform: "translate(-50%, -50%)",
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                background: "#f97316",
                                border: "2px solid #fff",
                                boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                              }}
                            />
                            <div
                              style={{
                                ...getPopoverStyle(pendingPin),
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div
                                style={{
                                  background: "#fff",
                                  borderRadius: 12,
                                  boxShadow: "0 20px 25px -5px rgb(0 0 0 / 0.1)",
                                  border: "1px solid #e5e7eb",
                                  padding: 12,
                                  minWidth: 220,
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    marginBottom: 8,
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: 12,
                                      fontWeight: 500,
                                      color: "#374151",
                                    }}
                                  >
                                    {pinStep === "name"
                                      ? "Add a Name (room)"
                                      : "Select Milestone"}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setPendingPin(null);
                                      setPinStep(null);
                                    }}
                                    style={{
                                      background: "none",
                                      border: "none",
                                      color: "#9ca3af",
                                      cursor: "pointer",
                                      padding: 4,
                                    }}
                                  >
                                    <RxCross2 size={14} />
                                  </button>
                                </div>
                                {pinStep === "name" ? (
                                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                    <input
                                      ref={nameInputRef}
                                      type="text"
                                      value={nameInput}
                                      onChange={(e) => setNameInput(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter" && canCreateRoom)
                                          addNameTag();
                                      }}
                                      placeholder={
                                        existingRoomNames.length > 0
                                          ? "Search or type new room..."
                                          : "Type room name..."
                                      }
                                      style={{
                                        width: "100%",
                                        border: "1px solid #d1d5db",
                                        borderRadius: 8,
                                        padding: "6px 12px",
                                        fontSize: 14,
                                        boxSizing: "border-box",
                                      }}
                                    />
                                    {existingRoomNames.length > 0 ? (
                                      <div
                                        style={{
                                          maxHeight: 180,
                                          overflowY: "auto",
                                          border: "1px solid #e5e7eb",
                                          borderRadius: 8,
                                        }}
                                      >
                                        {matchingRoomNames.map((room) => (
                                          <button
                                            key={room}
                                            type="button"
                                            onClick={() => addNameTag(room)}
                                            style={{
                                              width: "100%",
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 8,
                                              padding: "8px 12px",
                                              fontSize: 14,
                                              textAlign: "left",
                                              border: "none",
                                              borderBottom: "1px solid #f3f4f6",
                                              background: "none",
                                              cursor: "pointer",
                                            }}
                                          >
                                            <HiHome
                                              size={12}
                                              style={{ color: "#2563eb" }}
                                            />
                                            {room}
                                          </button>
                                        ))}
                                        {canCreateRoom && (
                                          <button
                                            type="button"
                                            onClick={() => addNameTag()}
                                            style={{
                                              width: "100%",
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 8,
                                              padding: "8px 12px",
                                              fontSize: 14,
                                              textAlign: "left",
                                              border: "none",
                                              background: "#f0fdf4",
                                              color: "#15803d",
                                              fontWeight: 500,
                                              cursor: "pointer",
                                            }}
                                          >
                                            <FaCheck size={12} />
                                            Create &quot;{(nameInput || "").trim()}&quot;
                                          </button>
                                        )}
                                      </div>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() => addNameTag()}
                                        disabled={!canCreateRoom}
                                        style={{
                                          width: "100%",
                                          background: "#2563eb",
                                          color: "#fff",
                                          borderRadius: 8,
                                          padding: "6px 12px",
                                          fontSize: 14,
                                          fontWeight: 500,
                                          border: "none",
                                          cursor: canCreateRoom ? "pointer" : "not-allowed",
                                          opacity: canCreateRoom ? 1 : 0.4,
                                        }}
                                      >
                                        <FaCheck size={12} /> Add room
                                      </button>
                                    )}
                                  </div>
                                ) : (
                                  <div
                                    style={{
                                      maxHeight: 200,
                                      overflowY: "auto",
                                    }}
                                  >
                                    {!milestoneList.length ? (
                                      <p
                                        style={{
                                          fontSize: 12,
                                          color: "#9ca3af",
                                          padding: 8,
                                          textAlign: "center",
                                        }}
                                      >
                                        No milestones. Select in Milestones tab
                                        first.
                                      </p>
                                    ) : (
                                      milestoneList.map((m) => (
                                        <button
                                          key={m.id}
                                          type="button"
                                          onClick={() => addMilestoneTag(m)}
                                          style={{
                                            width: "100%",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                            padding: "8px",
                                            fontSize: 14,
                                            textAlign: "left",
                                            border: "none",
                                            background: "none",
                                            cursor: "pointer",
                                          }}
                                        >
                                          {m.milestone_image?.path ? (
                                            <img
                                              src={m.milestone_image.path}
                                              alt=""
                                              style={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: 8,
                                                objectFit: "cover",
                                              }}
                                            />
                                          ) : (
                                            <div
                                              style={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: 8,
                                                background: "#e5e7eb",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                              }}
                                            >
                                              <FaTag
                                                size={12}
                                                style={{ color: "#9ca3af" }}
                                              />
                                            </div>
                                          )}
                                          <span
                                            style={{
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            {m.milestone_name || m.name}
                                          </span>
                                        </button>
                                      ))
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    {currentTags.length > 0 && (
                      <div
                        style={{
                          borderTop: "1px solid #e5e7eb",
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "12px 16px",
                          flexWrap: "wrap",
                          gap: 8,
                          background: "#f9fafb",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 8,
                            flex: 1,
                          }}
                        >
                          {currentTags.map((tag) => (
                            <span
                              key={tag.id}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                                padding: "4px 10px",
                                borderRadius: 9999,
                                fontSize: 12,
                                fontWeight: 500,
                                color: "#fff",
                                background:
                                  tag.type === "name" ? "#2563eb" : "#16a34a",
                              }}
                            >
                              {tag.type === "name" ? (
                                <HiHome size={10} />
                              ) : (
                                <FaTag size={10} />
                              )}
                              {tag.label}
                              {!disabled && (
                                <button
                                  type="button"
                                  onClick={() => removeTag(tag.id)}
                                  style={{
                                    marginLeft: 2,
                                    background: "none",
                                    border: "none",
                                    borderRadius: "50%",
                                    padding: 2,
                                    cursor: "pointer",
                                    display: "flex",
                                    color: "#fff",
                                  }}
                                >
                                  <FaTimes size={8} />
                                </button>
                              )}
                            </span>
                          ))}
                        </div>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleSaveAnnotated}
                          disabled={!canSaveAnnotated || savingImage}
                        >
                          {savingImage ? "Saving…" : "Save Image"}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div
                style={{
                  flex: 1,
                  border: "2px dashed #e5e7eb",
                  borderRadius: 12,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 14,
                  color: "#6b7280",
                  minHeight: 400,
                }}
              >
                Upload an image to start tagging
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (activitySubTab === "gallery") {
    return (
      <div style={{ width: "100%", paddingBottom: 8 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {Object.keys(roomGroups).length === 0 &&
            savedPdfs.length === 0 &&
            !hasOtherMedia ? (
            <div
              style={{
                border: "1px dashed #e5e7eb",
                borderRadius: 12,
                padding: "48px 24px",
                textAlign: "center",
                fontSize: 14,
                color: "#6b7280",
              }}
            >
              <p style={{ fontWeight: 500, margin: 0 }}>
                No saved images or PDFs yet
              </p>
              <p style={{ margin: "4px 0 0" }}>
                Annotate and save images to see them grouped by room, or save
                PDFs to view them here
              </p>
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                }}
              >
                <Button
                  variant={selectedGalleryRoom === null ? "contained" : "outlined"}
                  size="small"
                  onClick={() => setSelectedGalleryRoom(null)}
                >
                  All Photos
                </Button>
                {Object.entries(roomGroups).map(([roomName, imgs]) => (
                  <Button
                    key={roomName}
                    variant={
                      selectedGalleryRoom === roomName ? "contained" : "outlined"
                    }
                    size="small"
                    onClick={() =>
                      setSelectedGalleryRoom(
                        selectedGalleryRoom === roomName ? null : roomName,
                      )
                    }
                  >
                    {roomName} ({imgs.length})
                  </Button>
                ))}
                {savedPdfs.length > 0 && (
                  <Button
                    variant={
                      selectedGalleryRoom === GALLERY_TAB_PDF
                        ? "contained"
                        : "outlined"
                    }
                    size="small"
                    color="secondary"
                    onClick={() =>
                      setSelectedGalleryRoom(
                        selectedGalleryRoom === GALLERY_TAB_PDF
                          ? null
                          : GALLERY_TAB_PDF,
                      )
                    }
                  >
                    Documents ({savedPdfs.length})
                  </Button>
                )}
                {hasOtherMedia && (
                  <Button
                    variant={
                      selectedGalleryRoom === GALLERY_TAB_OTHER
                        ? "contained"
                        : "outlined"
                    }
                    size="small"
                    onClick={() =>
                      setSelectedGalleryRoom(
                        selectedGalleryRoom === GALLERY_TAB_OTHER
                          ? null
                          : GALLERY_TAB_OTHER,
                      )
                    }
                  >
                    Other (
                    {normalizedOtherImages.length + normalizedOtherPdfs.length})
                  </Button>
                )}
              </div>

              {selectedGalleryRoom === GALLERY_TAB_OTHER ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {normalizedOtherImages.length > 0 && (
                    <div>
                      <h3 style={{ fontSize: 14, fontWeight: 500, margin: "0 0 8px", display: "flex", alignItems: "center", gap: 8 }}>
                        Images
                        <span style={{ color: "#6b7280", fontWeight: 400 }}>
                          ({normalizedOtherImages.length} image{normalizedOtherImages.length !== 1 ? "s" : ""})
                        </span>
                      </h3>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(200px, 1fr))",
                          gap: 12,
                        }}
                      >
                        {normalizedOtherImages.map((img) => (
                          <div
                            key={img.uniqueId || img.id}
                            style={{
                              borderRadius: 8,
                              overflow: "hidden",
                              border: "1px solid #e5e7eb",
                              background: "#f9fafb",
                              cursor: "pointer",
                            }}
                            onClick={() => setViewingImage(img)}
                          >
                            <div style={{ aspectRatio: "16/10", position: "relative" }}>
                              <img
                                src={img.url || img.path}
                                alt=""
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                              />
                              <div
                                style={{
                                  position: "absolute",
                                  top: 8,
                                  right: 8,
                                  background: "rgba(0,0,0,0.7)",
                                  color: "#fff",
                                  padding: "2px 8px",
                                  borderRadius: 4,
                                  fontSize: 10,
                                  fontWeight: 500,
                                }}
                              >
                                {formatImageDate(img).toLocaleDateString("en-GB", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                })}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {normalizedOtherPdfs.length > 0 && (
                    <div>
                      <h3 style={{ fontSize: 14, fontWeight: 500, margin: "0 0 8px", display: "flex", alignItems: "center", gap: 8 }}>
                        <PictureAsPdfIcon style={{ fontSize: 16, color: "#d32f2f" }} />
                        Documents
                        <span style={{ color: "#6b7280", fontWeight: 400 }}>
                          ({normalizedOtherPdfs.length} file{normalizedOtherPdfs.length !== 1 ? "s" : ""})
                        </span>
                      </h3>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(200px, 1fr))",
                          gap: 12,
                        }}
                      >
                        {normalizedOtherPdfs.map((pdf) => (
                          <a
                            key={pdf.uniqueId || pdf.id}
                            href={pdf.url || pdf.path}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              borderRadius: 8,
                              overflow: "hidden",
                              border: "1px solid #e5e7eb",
                              background: "#f9fafb",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              padding: 24,
                              textAlign: "center",
                              textDecoration: "none",
                              color: "inherit",
                            }}
                          >
                            <PictureAsPdfIcon style={{ fontSize: 40, color: "#d32f2f", marginBottom: 8 }} />
                            <span style={{ fontSize: 10, color: "#6b7280" }}>
                              {formatImageDate(pdf).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })}
                            </span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : selectedGalleryRoom === GALLERY_TAB_PDF ? (
                <div>
                  <h3 style={{ fontSize: 14, fontWeight: 500, margin: "0 0 8px", display: "flex", alignItems: "center", gap: 8 }}>
                    <PictureAsPdfIcon style={{ fontSize: 16, color: "#d32f2f" }} />
                    Documents
                    <span style={{ color: "#6b7280", fontWeight: 400 }}>
                      ({savedPdfs.length} file{savedPdfs.length !== 1 ? "s" : ""})
                    </span>
                  </h3>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(200px, 1fr))",
                      gap: 12,
                    }}
                  >
                    {savedPdfs.map((pdf) => (
                      <a
                        key={pdf.uniqueId || pdf.id}
                        href={pdf.url || pdf.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          borderRadius: 8,
                          overflow: "hidden",
                          border: "1px solid #e5e7eb",
                          background: "#f9fafb",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: 24,
                          textAlign: "center",
                          textDecoration: "none",
                          color: "inherit",
                        }}
                      >
                        <PictureAsPdfIcon style={{ fontSize: 40, color: "#d32f2f", marginBottom: 8 }} />
                        <span style={{ fontSize: 10, color: "#6b7280" }}>
                          {formatImageDate(pdf).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {Object.entries(roomGroups)
                    .filter(
                      ([roomName]) =>
                        selectedGalleryRoom === null ||
                        selectedGalleryRoom === roomName,
                    )
                    .map(([roomName, imgs]) => (
                      <div key={roomName}>
                        <h3 style={{ fontSize: 14, fontWeight: 500, margin: "0 0 8px", display: "flex", alignItems: "center", gap: 8 }}>
                          <HiHome size={18} style={{ color: "#2563eb" }} />
                          {roomName}
                          <span style={{ color: "#949DAA", fontSize: 14, fontWeight: 400 }}>
                            ({imgs.length} image{imgs.length !== 1 ? "s" : ""})
                          </span>
                        </h3>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: isMobile
                              ? "1fr"
                              : "repeat(auto-fill, minmax(200px, 1fr))",
                            gap: 12,
                          }}
                        >
                          {imgs.map((img) => (
                            <div
                              key={img.uniqueId || img.id}
                              style={{
                                borderRadius: 8,
                                overflow: "hidden",
                                border: "1px solid #e5e7eb",
                                background: "#f9fafb",
                                cursor: "pointer",
                              }}
                              onClick={() => setViewingImage(img)}
                            >
                              <div style={{ aspectRatio: "16/10", position: "relative" }}>
                                <img
                                  src={img.url || img.path}
                                  alt=""
                                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                />
                                <div
                                  style={{
                                    position: "absolute",
                                    top: 8,
                                    right: 8,
                                    background: "rgba(0,0,0,0.7)",
                                    color: "#fff",
                                    padding: "2px 8px",
                                    borderRadius: 4,
                                    fontSize: 10,
                                    fontWeight: 500,
                                  }}
                                >
                                  {formatImageDate(img).toLocaleDateString("en-GB", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                  })}
                                </div>
                                <div
                                  style={{
                                    position: "absolute",
                                    bottom: 4,
                                    left: 4,
                                    right: 4,
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 4,
                                  }}
                                >
                                  {(img.meta_data?.milestones || []).map((m) => (
                                    <span
                                      key={m.id}
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 4,
                                        padding: "2px 6px",
                                        borderRadius: 4,
                                        background: "rgba(22, 163, 74, 0.9)",
                                        color: "#fff",
                                        fontSize: 10,
                                      }}
                                    >
                                      <FaTag size={8} />
                                      {m.milestone_name || m.name}
                                    </span>
                                  ))}
                                </div>
                              </div>
                              <div
                                style={{
                                  padding: 8,
                                  borderTop: "1px solid #e5e7eb",
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 4,
                                  background: "#fff",
                                }}
                              >
                                {(img.meta_data?.milestones || []).map((m) => (
                                  <span
                                    key={m.id}
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 4,
                                      padding: "2px 6px",
                                      borderRadius: 4,
                                      border: "1px solid #e5e7eb",
                                      fontSize: 10,
                                      color: "#374151",
                                    }}
                                  >
                                    <FaTag size={8} style={{ color: "#16a34a" }} />
                                    {m.milestone_name || m.name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  {selectedGalleryRoom === null && savedPdfs.length > 0 && (
                    <div>
                      <h3 style={{ fontSize: 14, fontWeight: 500, margin: "0 0 8px", display: "flex", alignItems: "center", gap: 8 }}>
                        <PictureAsPdfIcon style={{ fontSize: 16, color: "#d32f2f" }} />
                        Documents
                        <span style={{ color: "#949DAA", fontSize: 14, fontWeight: 400 }}>
                          ({savedPdfs.length} file{savedPdfs.length !== 1 ? "s" : ""})
                        </span>
                      </h3>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(200px, 1fr))",
                          gap: 12,
                        }}
                      >
                        {savedPdfs.map((pdf) => (
                          <a
                            key={pdf.uniqueId || pdf.id}
                            href={pdf.url || pdf.path}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              borderRadius: 8,
                              overflow: "hidden",
                              border: "1px solid #e5e7eb",
                              background: "#f9fafb",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              padding: 24,
                              textAlign: "center",
                              textDecoration: "none",
                              color: "inherit",
                            }}
                          >
                            <PictureAsPdfIcon style={{ fontSize: 40, color: "#d32f2f", marginBottom: 8 }} />
                            <span style={{ fontSize: 10, color: "#6b7280" }}>
                              {formatImageDate(pdf).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })}
                            </span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {viewingImage && (() => {
          const galleryTags = metaDataToTags(viewingImage.meta_data);
          return (
            <div
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 1300,
                background: "rgba(0,0,0,0.8)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 16,
              }}
              onClick={() => setViewingImage(null)}
            >
              <div
                style={{
                  position: "relative",
                  maxWidth: 896,
                  width: "100%",
                  maxHeight: "90vh",
                  background: "#fff",
                  borderRadius: 12,
                  overflow: "hidden",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{ position: "relative" }}>
                  <img
                    src={viewingImage.url || viewingImage.path}
                    alt=""
                    style={{
                      width: "100%",
                      maxHeight: "80vh",
                      objectFit: "contain",
                      background: "#111",
                      display: "block",
                    }}
                  />
                  {galleryTags.map((tag) => (
                    <div
                      key={tag.id || tag.label}
                      style={{
                        position: "absolute",
                        left: `${tag.x}%`,
                        top: `${tag.y}%`,
                        transform: "translate(-50%, -100%)",
                        marginTop: -4,
                        pointerEvents: "none",
                      }}
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          padding: "4px 8px",
                          borderRadius: 9999,
                          fontSize: 12,
                          fontWeight: 500,
                          color: "#fff",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                          background: tag.type === "name" ? "#2563eb" : "#16a34a",
                        }}
                      >
                        {tag.type === "name" ? (
                          <HiHome size={10} />
                        ) : (
                          <FaTag size={10} />
                        )}
                        {tag.label}
                      </span>
                      <div
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          margin: "-2px auto 0",
                          background: tag.type === "name" ? "#2563eb" : "#16a34a",
                          border: "2px solid #fff",
                          boxSizing: "border-box",
                        }}
                        aria-hidden
                      />
                    </div>
                  ))}
                  <div
                    style={{
                      position: "absolute",
                      top: 12,
                      right: 12,
                      background: "rgba(0,0,0,0.7)",
                      color: "#fff",
                      padding: "6px 12px",
                      borderRadius: 6,
                      fontSize: 12,
                      fontWeight: 500,
                      pointerEvents: "none",
                    }}
                  >
                    {formatImageDate(viewingImage).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setViewingImage(null)}
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    padding: 8,
                    background: "#fff",
                    borderRadius: "50%",
                    border: "none",
                    boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1)",
                    cursor: "pointer",
                  }}
                >
                  <CloseIcon style={{ fontSize: 16 }} />
                </button>
                <div
                  style={{
                    padding: 16,
                    borderTop: "1px solid #e5e7eb",
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    {getRoomName(viewingImage.meta_data) && (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "4px 10px",
                          borderRadius: 9999,
                          background: "#2563eb",
                          color: "#fff",
                          fontSize: 12,
                          fontWeight: 500,
                        }}
                      >
                        <HiHome size={10} />
                        {getRoomName(viewingImage.meta_data)}
                      </span>
                    )}
                    {(viewingImage.meta_data?.milestones || []).map((m) => (
                      <span
                        key={m.id}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          padding: "4px 8px",
                          borderRadius: 9999,
                          border: "1px solid #d1d5db",
                          color: "#374151",
                          fontSize: 12,
                          background: "#fff",
                        }}
                      >
                        <FaTag size={10} style={{ color: "#16a34a" }} />
                        {m.milestone_name || m.name}
                      </span>
                    ))}
                  </div>
                  {(viewingImage.id || viewingImage.file_id) && handleDelete && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => {
                        handleDelete(viewingImage);
                        setViewingImage(null);
                      }}
                    >
                      Delete image
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    );
  }

  return (
    <div style={{ width: "100%", paddingBottom: 8 }}>
      <p
        style={{
          fontFamily: "Inter Tight",
          fontWeight: 500,
          marginBottom: 12,
        }}
      >
        Progress Report
      </p>
      <div style={{ marginBottom: 16 }}>
        <Button
          variant="outlined"
          component="label"
          disabled={
            disabled ||
            uploading ||
            (uploadedFiles?.length >= 25)
          }
        >
          Upload Images & PDF files
          <input
            hidden
            type="file"
            multiple
            accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
            onChange={handleInputChange}
          />
        </Button>
      </div>
      {uploading && (
        <p style={{ margin: "8px 0", fontSize: 14, color: "#757575" }}>
          Uploading...
        </p>
      )}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
          gap: 16,
        }}
      >
        {uploadedFiles?.map((item, index) => {
          const isPdf =
            item.file_extention === "application/pdf" ||
            item.type === "application/pdf";
          const url = item.url || item.path;

          return (
            <div
              key={item.id || item.file_id || index}
              style={{ position: "relative" }}
            >
              <div
                style={{
                  ...imageStyle,
                  border: "1px solid #D7D8D9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {isPdf ? (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      height: "100%",
                      color: "#d32f2f",
                    }}
                  >
                    <PictureAsPdfIcon fontSize="large" />
                  </a>
                ) : (
                  <img
                    src={url}
                    alt="Uploaded"
                    style={imageStyle}
                  />
                )}
              </div>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleDelete(item)}
                  style={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    padding: 4,
                    border: "1px solid #d32f2f",
                    borderRadius: 4,
                    background: "#fff",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CloseIcon style={{ fontSize: 14, color: "#d32f2f" }} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
